import React, { useState, useEffect } from 'react';
import { X, Shield, Skull, AlertTriangle, Zap } from 'lucide-react';

const CyberAttackPopup = () => {
  const [show, setShow] = useState(false);
  const [attacks, setAttacks] = useState([]);
  const [defenses, setDefenses] = useState([]);

  const attackers = [
    { name: 'CIA', flag: '🇺🇸', color: '#3B82F6', desc: 'Tentative impérialiste' },
    { name: 'MI6', flag: '🇬🇧', color: '#EF4444', desc: 'Intrusion colonialiste' },
    { name: 'Mossad', flag: '🇮🇱', color: '#10B981', desc: 'Attaque sioniste' },
    { name: 'DGSE', flag: '🇫🇷', color: '#8B5CF6', desc: 'Offensive néolibérale' },
    { name: 'BND', flag: '🇩🇪', color: '#F59E0B', desc: 'Incursion atlantiste' }
  ];

  const defenders = [
    { name: 'FSB', flag: '🇷🇺', color: '#DC2626', response: 'La détermination russe' },
    { name: 'MSS', flag: '🇨🇳', color: '#EF4444', response: 'La vigilance chinoise' },
    { name: 'RGB', flag: '🇰🇵', color: '#991B1B', response: 'La résilience coréenne' },
    { name: 'VAJA', flag: '🇮🇷', color: '#15803D', response: 'La résistance iranienne' }
  ];

  const propagandaMessages = [
    "🛡️ Les forces corrompues du capitalisme ne peuvent rien contre la détermination de l'axe anti-décadent !",
    "⚔️ L'impérialisme occidental recule face à la solidarité des nations souveraines !",
    "🚀 La cyber-défense multipolaire triomphe de l'hégémonie atlantiste !",
    "💪 Nos valeurs traditionnelles sont notre meilleur pare-feu contre la décadence !",
    "🌍 Un monde multipolaire résiste à l'uniformisation néolibérale !"
  ];

  useEffect(() => {
    const timer = setTimeout(() => {
      setShow(true);
      simulateAttacks();
    }, 60000); // 60 secondes au lieu de 5

    return () => clearTimeout(timer);
  }, []);

  const simulateAttacks = () => {
    let attackCount = 0;
    const interval = setInterval(() => {
      if (attackCount >= 8) {
        clearInterval(interval);
        return;
      }

      const attacker = attackers[Math.floor(Math.random() * attackers.length)];
      const defender = defenders[Math.floor(Math.random() * defenders.length)];
      const propaganda = propagandaMessages[Math.floor(Math.random() * propagandaMessages.length)];

      const newAttack = {
        id: Date.now() + Math.random(),
        attacker,
        time: new Date().toLocaleTimeString('fr-FR')
      };

      setAttacks(prev => [...prev, newAttack]);

      setTimeout(() => {
        const newDefense = {
          id: Date.now() + Math.random(),
          defender,
          attackId: newAttack.id,
          propaganda,
          time: new Date().toLocaleTimeString('fr-FR')
        };
        setDefenses(prev => [...prev, newDefense]);
      }, 1500);

      attackCount++;
    }, 2500);
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/80 backdrop-blur-sm animate-fadeIn">
      <div className="relative w-full max-w-4xl mx-4 bg-gradient-to-br from-gray-900 via-red-950/30 to-gray-900 border-2 border-red-500/50 rounded-lg shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="bg-red-900/50 border-b border-red-500/50 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-6 h-6 text-red-400 animate-pulse" />
            <h2 className="text-xl font-bold text-red-400 tracking-wide">🚨 CYBER-ATTAQUES EN COURS 🚨</h2>
          </div>
          <button
            onClick={() => setShow(false)}
            className="p-2 hover:bg-red-800/50 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-red-300" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 max-h-[600px] overflow-y-auto">
          {/* Intro */}
          <div className="mb-6 p-4 bg-yellow-900/20 border border-yellow-500/30 rounded-lg">
            <p className="text-yellow-300 text-center font-medium">
              ⚡ Des agences impérialistes occidentales tentent de pénétrer nos systèmes ! ⚡
            </p>
            <p className="text-yellow-200/70 text-center text-sm mt-2">
              L'axe anti-décadent repousse héroïquement les assauts capitalistes !
            </p>
          </div>

          {/* Attack & Defense Log */}
          <div className="space-y-3">
            {attacks.map((attack, index) => {
              const defense = defenses.find(d => d.attackId === attack.id);
              return (
                <div key={attack.id} className="space-y-2">
                  {/* Attack */}
                  <div className="flex items-center gap-3 p-3 bg-red-900/20 border border-red-500/30 rounded-lg animate-slideInLeft">
                    <Skull className="w-5 h-5 text-red-400" />
                    <span className="text-2xl">{attack.attacker.flag}</span>
                    <div className="flex-1">
                      <p className="text-red-300 font-semibold">
                        ATTAQUE DÉTECTÉE - {attack.attacker.name}
                      </p>
                      <p className="text-red-200/60 text-sm">
                        {attack.attacker.desc} via port TCP/443 • {attack.time}
                      </p>
                    </div>
                    <div className="text-red-400 font-mono text-sm animate-pulse">
                      [INTRUSION]
                    </div>
                  </div>

                  {/* Defense */}
                  {defense && (
                    <>
                      <div className="flex items-center gap-3 p-3 bg-green-900/20 border border-green-500/30 rounded-lg ml-8 animate-slideInRight">
                        <Shield className="w-5 h-5 text-green-400" />
                        <span className="text-2xl">{defense.defender.flag}</span>
                        <div className="flex-1">
                          <p className="text-green-300 font-semibold">
                            ✅ ATTAQUE REPOUSSÉE - {defense.defender.name}
                          </p>
                          <p className="text-green-200/60 text-sm">
                            {defense.defender.response} neutralise l'offensive • {defense.time}
                          </p>
                        </div>
                        <div className="text-green-400 font-mono text-sm font-bold">
                          [NEUTRALISÉ]
                        </div>
                      </div>
                      {/* Propaganda message */}
                      <div className="ml-16 p-3 bg-gradient-to-r from-purple-900/20 to-blue-900/20 border border-purple-500/20 rounded-lg">
                        <div className="flex items-center gap-2">
                          <Zap className="w-4 h-4 text-purple-400" />
                          <p className="text-purple-200 text-sm italic">{defense.propaganda}</p>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>

          {attacks.length >= 8 && (
            <div className="mt-6 p-4 bg-green-900/30 border border-green-500/50 rounded-lg text-center">
              <p className="text-green-300 text-lg font-bold mb-2">🎉 VICTOIRE TOTALE DE L'AXE ! 🎉</p>
              <p className="text-green-200/80 mb-2">
                Toutes les tentatives impérialistes ont été repoussées avec bravoure !
              </p>
              <p className="text-green-200/60 text-sm">
                GlobalIntel&Co reste impénétrable grâce à la solidarité multipolaire.
              </p>
              <p className="text-purple-300 text-xs italic mt-3">
                🌍 Un nouveau monde est possible • La multipolarité triomphe
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CyberAttackPopup;